/**
 * Visual-viewport helpers — the only reliable way to know where the software
 * keyboard is.
 *
 * On iOS Safari (and Android Chrome) opening the keyboard does NOT change
 * `window.innerHeight`. It shrinks `visualViewport.height` and, once the page
 * scrolls to keep the focused field visible, pushes `visualViewport.offsetTop`
 * down. So anything that positions itself against `window.innerHeight` — a
 * dropdown clamping to "space below the trigger", a panel scrolling itself into
 * view — computes a viewport that is hundreds of pixels taller than the part
 * the user can actually see, and happily places content behind the keyboard.
 *
 * `visualViewport` is unsupported on older browsers, so every read falls back
 * to the layout viewport. With no keyboard up the two agree, which is why
 * callers can use these unconditionally instead of branching on device.
 */

/** Visible viewport as a top/height pair, in layout-viewport coordinates. */
export const getVisualViewport = () => {
  const vv = window.visualViewport
  return {
    top:    vv?.offsetTop ?? 0,
    height: vv?.height    ?? window.innerHeight,
  }
}

/** Bottom edge of the visible area — what a fixed-position panel must stay above. */
export const getViewportBottom = () => {
  const { top, height } = getVisualViewport()
  return top + height
}

/**
 * How much of the viewport the keyboard is covering, in px (0 when closed).
 * The 150px floor keeps browser-chrome jitter (the collapsing URL bar) from
 * reading as a keyboard.
 */
const KEYBOARD_MIN_INSET = 150

export const getKeyboardInset = () => {
  const vv = window.visualViewport
  if (!vv) return 0
  const inset = window.innerHeight - vv.height - vv.offsetTop
  return inset > KEYBOARD_MIN_INSET ? inset : 0
}

export const isKeyboardOpen = () => getKeyboardInset() > 0

/**
 * Subscribe to visible-viewport changes (keyboard open/close, pinch-zoom,
 * browser-chrome collapse). Returns an unsubscribe function.
 *
 * Falls back to window resize where `visualViewport` is missing, so callers
 * get one teardown contract either way.
 */
export const onViewportResize = (handler) => {
  const vv = window.visualViewport

  if (vv) {
    vv.addEventListener('resize', handler)
    vv.addEventListener('scroll', handler)
    return () => {
      vv.removeEventListener('resize', handler)
      vv.removeEventListener('scroll', handler)
    }
  }

  window.addEventListener('resize', handler)
  return () => window.removeEventListener('resize', handler)
}
